<template>
  <b-modal
    :visible="isUploadImagesModalActive"
    title="Upload Foto QC Reject"
    size="lg"
    hide-footer
    @hidden="onHidden"
  >
    <div v-if="rejectItem">
      <b-row class="mb-1">
        <b-col
          cols="12"
          md="6"
        >
          <strong>Item Code:</strong> {{ rejectItem.item_code }}
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <strong>Nama Produk:</strong> {{ rejectItem.product_name }}
        </b-col>
      </b-row>

      <b-alert
        show
        variant="light-primary"
        class="mb-1"
      >
        Foto tersimpan {{ currentImages.length }}/4. Sisa slot upload: {{ remainingImageSlots }}.
      </b-alert>

      <b-form-group label="Upload Foto">
        <b-form-file
          v-model="files"
          multiple
          accept="image/jpeg,image/png,image/webp"
          browse-text="Pilih Foto"
          placeholder="Pilih satu atau beberapa foto"
          drop-placeholder="Drop foto di sini"
          :state="selectedFiles.length ? !isOverImageLimit : null"
          :disabled="isLoading || remainingImageSlots === 0"
        />
      </b-form-group>

      <div
        v-if="selectedFiles.length"
        class="mb-1"
      >
        <h6 class="mb-50">
          Foto yang akan diupload
        </h6>
        <div class="selected-file-list">
          <b-badge
            v-for="file in selectedFiles"
            :key="`${file.name}-${file.size}-${file.lastModified}`"
            variant="light-secondary"
            class="mr-50 mb-50"
          >
            {{ file.name }}
          </b-badge>
        </div>
        <small :class="isOverImageLimit ? 'text-danger' : 'text-muted'">
          Maksimal 4 foto per item. Pilihan saat ini {{ selectedFiles.length }} foto, sisa slot {{ remainingImageSlots }}.
        </small>
      </div>

      <div class="d-flex justify-content-end mb-1">
        <b-button
          variant="primary"
          :disabled="!selectedFiles.length || isOverImageLimit || isLoading"
          @click="submitUpload"
        >
          <b-spinner
            v-if="isLoading"
            small
            class="mr-50"
          />
          Upload Foto
        </b-button>
      </div>

      <div>
        <h6 class="mb-1">
          Foto Tersimpan
        </h6>

        <b-row v-if="currentImages.length">
          <b-col
            v-for="image in currentImages"
            :key="image.id || image.image_url"
            cols="6"
            md="4"
            class="mb-1"
          >
            <div class="qc-reject-image-card">
              <b-link
                :href="image.image_url"
                target="_blank"
              >
                <b-img
                  :src="image.image_url"
                  fluid
                  thumbnail
                  class="w-100 qc-reject-image"
                />
              </b-link>
              <b-button
                size="sm"
                variant="danger"
                class="qc-reject-image-delete"
                :disabled="deletingImageId === image.id"
                @click="deleteImage(image)"
              >
                <b-spinner
                  v-if="deletingImageId === image.id"
                  small
                />
                <span v-else>Hapus</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
        <p
          v-else
          class="text-muted mb-0"
        >
          Belum ada foto untuk item ini.
        </p>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCol,
  BFormFile,
  BFormGroup,
  BImg,
  BLink,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import store from '@/store'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormFile,
    BFormGroup,
    BImg,
    BLink,
    BModal,
    BRow,
    BSpinner,
  },
  props: {
    isUploadImagesModalActive: {
      type: Boolean,
      required: true,
    },
    rejectItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      files: [],
      isLoading: false,
      deletingImageId: null,
      currentImages: [],
    }
  },
  computed: {
    selectedFiles() {
      if (!this.files) {
        return []
      }

      if (Array.isArray(this.files)) {
        return this.files
      }

      if (this.files instanceof File) {
        return [this.files]
      }

      return Array.from(this.files)
    },
    remainingImageSlots() {
      return Math.max(0, 4 - this.currentImages.length)
    },
    isOverImageLimit() {
      return this.selectedFiles.length > this.remainingImageSlots
    },
  },
  watch: {
    rejectItem: {
      immediate: true,
      handler(value) {
        this.currentImages = value && value.images ? value.images : []
      },
    },
  },
  methods: {
    onHidden() {
      this.files = []
      this.isLoading = false
      this.deletingImageId = null
      this.$emit('update:is-upload-images-modal-active', false)
    },
    submitUpload() {
      if (!this.rejectItem || !this.selectedFiles.length || this.isOverImageLimit) {
        return
      }

      this.isLoading = true
      store.dispatch('app-qc-reject/uploadQcRejectImages', {
        id: this.rejectItem.id,
        files: this.selectedFiles,
      })
        .then(response => {
          this.currentImages = response.data.data.images || []
          this.$emit('refetch-data')
          this.$emit('update:is-upload-images-modal-active', false)
        })
        .finally(() => {
          this.files = []
          this.isLoading = false
        })
    },
    deleteImage(image) {
      if (!image || !image.id) {
        return
      }

      this.deletingImageId = image.id
      store.dispatch('app-qc-reject/deleteQcRejectImage', image.id)
        .then(response => {
          this.currentImages = response.data.data && response.data.data.images
            ? response.data.data.images
            : this.currentImages.filter(currentImage => currentImage.id !== image.id)
          this.$emit('refetch-data')
        })
        .finally(() => {
          this.deletingImageId = null
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.qc-reject-image-card {
  position: relative;
}

.qc-reject-image {
  height: 140px;
  object-fit: cover;
}

.qc-reject-image-delete {
  position: absolute;
  right: 8px;
  top: 8px;
}
</style>
